/**
 * Parser for the support bot's `%= propose_change =%` blocks.
 *
 * The bot emits these blocks (built server-side by
 * `buildProposedChange`, see server/src/db/controllers/proposed-change/*)
 * inline in its assistant message text. This module splits a message into
 * ordinary text segments and proposal segments so the renderer can show
 * normal markdown for the prose and an interactive approval card for each
 * proposal.
 *
 * Delimiters MUST stay in sync with the server's
 * `PROPOSE_CHANGE_BLOCK_START` / `PROPOSE_CHANGE_BLOCK_END`.
 *
 * Robustness: a block that fails to parse (truncated mid-stream, malformed
 * JSON, unknown version) is left as plain text rather than throwing — the
 * operator still sees the raw content and nothing crashes.
 */

const BLOCK_START = '%= propose_change'
const BLOCK_END = '=%'
const BLOCK_START_KEYWORD = 'propose_change'
const SUPPORTED_VERSION = 1

/**
 * Matches a full `%= propose_change ... =%` block, capturing the JSON
 * payload between the delimiters. Non-greedy so adjacent blocks don't get
 * merged; `[\s\S]` so the payload can span newlines.
 */
const BLOCK_REGEX = /%= propose_change\s*([\s\S]*?)\s*=%/g

/**
 * Mirror of the server's `ProposedChangeOperation`:
 *  - `update` patches an existing record (staleness-guarded)
 *  - `create` proposes a brand-new record (`target_entity_id` is null)
 */
export type ProposeChangeOperation = 'update' | 'create'

export interface ProposeChangeExecutePayload {
    operation: ProposeChangeOperation
    target_entity_type: string
    /** Null for create proposals — the record does not exist yet. */
    target_entity_id: string | null
    /**
     * Required for create (auth + clinic FK). Optional for update.
     * Prefer this over `display.clinic_id` when applying.
     */
    clinic_id: string | null
    patch: Record<string, unknown>
    captured_before: Record<string, unknown>
}

export interface ProposeChangeFieldDiff {
    field: string
    before: unknown
    after: unknown
}

export interface ProposeChangeDisplay {
    entity_display_name: string
    clinic_id: string | null
    summary: string | null
    side_effects: string[]
    field_diff: ProposeChangeFieldDiff[]
    risk_tier: string
}

export interface ParsedProposal {
    execute: ProposeChangeExecutePayload
    display: ProposeChangeDisplay
    /**
     * True when the block was previously applied and the message content
     * was rewritten to record that (or when the dashboard has a matching
     * localStorage entry). Cards with this set render as Applied.
     */
    applied?: boolean
}

export type MessageSegment =
    | { type: 'text'; value: string }
    | { type: 'proposal'; proposal: ParsedProposal }
    /**
     * Incomplete `%= propose_change …` still being streamed. Renderers should
     * show a placeholder instead of the raw delimiter/JSON.
     */
    | { type: 'proposal_pending' }

export interface SplitMessageContentOptions {
    /**
     * When true, hide a trailing incomplete proposal block behind a
     * `proposal_pending` segment instead of showing raw `%= …` text. Use for
     * the live streaming bubble only — settled messages keep the raw text so
     * operators can still see malformed / truncated blocks.
     */
    hideIncompleteBlocks?: boolean
}

/**
 * Split assistant message content into ordered text + proposal segments.
 * Text with no proposal blocks returns a single text segment.
 */
export function splitMessageContent(
    content: string,
    options?: SplitMessageContentOptions,
): MessageSegment[] {
    const hideIncomplete = options?.hideIncompleteBlocks === true

    // Fast path when there's no complete-or-incomplete proposal marker.
    if (!content.includes(BLOCK_START) && !content.includes('%=')) {
        return [{ type: 'text', value: content }]
    }

    const segments: MessageSegment[] = []
    let lastIndex = 0
    // `matchAll` over a fresh regex would also work, but we need the match
    // indices to slice the surrounding text, so iterate with exec.
    const regex = new RegExp(BLOCK_REGEX)
    let match: RegExpExecArray | null = regex.exec(content)

    while (match !== null) {
        const [fullMatch, jsonPayload] = match
        const matchStart = match.index

        // Text before this block.
        if (matchStart > lastIndex) {
            segments.push({
                type: 'text',
                value: content.slice(lastIndex, matchStart),
            })
        }

        const proposal = tryParseProposal(jsonPayload)
        if (proposal) {
            segments.push({ type: 'proposal', proposal })
        } else {
            // Couldn't parse — keep the raw block as text so nothing is lost.
            segments.push({ type: 'text', value: fullMatch })
        }

        lastIndex = matchStart + fullMatch.length
        match = regex.exec(content)
    }

    // Trailing text after the last complete block (or the whole message if
    // none). While streaming, this is where an in-progress block lives.
    if (lastIndex < content.length) {
        const trailing = content.slice(lastIndex)
        if (hideIncomplete) {
            const incompleteAt = findIncompleteProposeBlockStart(trailing)
            if (incompleteAt >= 0) {
                if (incompleteAt > 0) {
                    segments.push({
                        type: 'text',
                        value: trailing.slice(0, incompleteAt),
                    })
                }
                segments.push({ type: 'proposal_pending' })
                return segments
            }
        }
        segments.push({ type: 'text', value: trailing })
    }

    return segments
}

/**
 * Index of a trailing incomplete `%= propose_change` (or a prefix like
 * `%=` / `%= propose`) within `text`, or -1 if none.
 */
function findIncompleteProposeBlockStart(text: string): number {
    // Prefer the explicit start marker once enough characters have arrived.
    const fullStart = text.lastIndexOf(BLOCK_START)
    if (fullStart >= 0) {
        const after = text.slice(fullStart)
        // A closed block in the trailing slice would already have been
        // consumed by BLOCK_REGEX; treat a remaining open start as pending.
        if (!after.includes(BLOCK_END)) {
            return fullStart
        }
    }

    // Soft match: the stream is still typing the delimiter (`%=`, `%= p`, …).
    const soft = text.lastIndexOf('%=')
    if (soft < 0) return -1

    const afterEquals = text.slice(soft + 2)
    // Already closed somewhere after this `%=` — not incomplete.
    if (afterEquals.includes(BLOCK_END)) return -1

    const trimmed = afterEquals.replace(/^\s*/, '')
    if (trimmed.length === 0) return soft

    const firstToken = trimmed.split(/\s/, 1)[0] ?? ''
    if (
        BLOCK_START_KEYWORD.startsWith(firstToken) ||
        firstToken.startsWith(BLOCK_START_KEYWORD)
    ) {
        return soft
    }

    return -1
}

/**
 * Parse + validate a block's JSON payload into a ParsedProposal. Returns
 * null on any problem (bad JSON, wrong version, missing required fields) so
 * callers can fall back to rendering the raw text.
 */
function tryParseProposal(jsonPayload: string): ParsedProposal | null {
    let parsed: unknown
    try {
        parsed = JSON.parse(jsonPayload)
    } catch {
        return null
    }

    if (!isRecord(parsed)) return null
    if (parsed.version !== SUPPORTED_VERSION) return null

    const execute = parsed.execute
    const display = parsed.display
    if (!isRecord(execute) || !isRecord(display)) return null

    const operation = parseOperation(execute.operation)
    if (!operation) return null

    // Update proposals must target an existing record; create proposals
    // must NOT (any id would be meaningless — nothing exists yet).
    const targetEntityId =
        typeof execute.target_entity_id === 'string'
            ? execute.target_entity_id
            : null
    if (operation === 'update' && !targetEntityId) return null

    if (
        typeof execute.target_entity_type !== 'string' ||
        !isRecord(execute.patch) ||
        !isRecord(execute.captured_before)
    ) {
        return null
    }

    const displayClinicId =
        typeof display.clinic_id === 'string' ? display.clinic_id : null
    const executeClinicId =
        typeof execute.clinic_id === 'string' ? execute.clinic_id : null
    if (operation === 'create' && !executeClinicId && !displayClinicId) {
        return null
    }

    return {
        execute: {
            operation,
            target_entity_type: execute.target_entity_type,
            target_entity_id: operation === 'create' ? null : targetEntityId,
            clinic_id: executeClinicId ?? displayClinicId,
            patch: execute.patch,
            captured_before: execute.captured_before,
        },
        display: {
            entity_display_name:
                typeof display.entity_display_name === 'string'
                    ? display.entity_display_name
                    : 'Proposed change',
            clinic_id: displayClinicId ?? executeClinicId,
            summary:
                typeof display.summary === 'string' ? display.summary : null,
            side_effects: Array.isArray(display.side_effects)
                ? display.side_effects.filter(
                      (s): s is string => typeof s === 'string',
                  )
                : [],
            field_diff: parseFieldDiff(display.field_diff),
            risk_tier:
                typeof display.risk_tier === 'string'
                    ? display.risk_tier
                    : 'low',
        },
        ...(parsed.applied === true ? { applied: true } : {}),
    }
}

/**
 * Parse the block's operation. Blocks emitted before create support have
 * no `operation` key — treat those as updates. Anything unrecognized is
 * rejected (the whole block falls back to plain text).
 */
function parseOperation(value: unknown): ProposeChangeOperation | null {
    if (value === undefined || value === 'update') return 'update'
    if (value === 'create') return 'create'
    return null
}

function parseFieldDiff(value: unknown): ProposeChangeFieldDiff[] {
    if (!Array.isArray(value)) return []
    return value
        .filter(isRecord)
        .filter(entry => typeof entry.field === 'string')
        .map(entry => ({
            field: entry.field as string,
            before: entry.before ?? null,
            after: entry.after ?? null,
        }))
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export {
    BLOCK_START as PROPOSE_CHANGE_BLOCK_START,
    BLOCK_END as PROPOSE_CHANGE_BLOCK_END,
}
